import { useState } from 'react';
import { Grid, Button, TextField } from '@material-ui/core';
import { DialogActions, DialogContentText, DialogContent, Dialog, DialogTitle, Checkbox, FormControlLabel } from '@material-ui/core';
import { FormHelperText, FormControl, Select, MenuItem, InputLabel } from '@material-ui/core/';
import { userTypes, userTypesString } from '../../app/enums';
import { Alert, AlertTitle } from '@material-ui/lab';
import SuccessDialog from '../Dialogs/SuccessDialog';
import { Users, Locations } from '../../api/api';
import { UserType } from '../../models/user.interface';
import { Location } from '../../models/location.interface';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { defaultUserType } from '../../pages/Users';
import { isEmpty } from 'lodash';

const useStyles = makeStyles((theme: Theme) =>
  createStyles ({
    gridSpacer: {
      minHeight: '12px',
    },
  }),
);

export interface ICreateUserDialog {
  open: boolean
  onClose: () => void
  locationList: Location[]
}

export const CreateUserDialog = ({ open, onClose, locationList }: ICreateUserDialog) => {
  const classes = useStyles(); 
  const [ saved, setSaved ] = useState<boolean>(false);
  const [ user, setUser ] = useState<UserType>(defaultUserType);
  const [ userErrors, setUserErrors ] = useState<UserType>(defaultUserType);
  const [ locationError, setLocationError ] = useState<string>('');
  const [ serviceError, setServiceError ] = useState<string>('');
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setUser({...user, [e.target.id]: e.target.value})
  }

  const handleEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUser({...user, enabled: e.target.checked})
  };

  const handleUserTypeChange = (e: React.FormEvent<EventTarget>) => {
    let target = e.target as HTMLInputElement

    if(target.value === userTypes.INTERNAL){
      setUser({
        ...user, 
        userType: target.value,
        locations: []
      })
    } else {
      setUser({...user, userType: target.value})
    }
  }

  const validateField = (e: React.FormEvent<EventTarget>) => {
    const target = e.target as HTMLInputElement

    if(target.value === undefined || target.value.length < 1)
      setUserErrors({...userErrors, [target.id]: 'Required'})
    else
      setUserErrors({...userErrors, [target.id]: ''})
  }
 
  const onCloseHandler = () => {
    onClose()
    setSaved(false)
    setUser(defaultUserType);
    setServiceError('');
    setUserErrors(defaultUserType)
  }

  const validateEmail = () => {
    if(isEmpty(user.email)){
      setUserErrors({
        ...userErrors, 
        email: 'Required'
      })
    } else if(user.email.search(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/)){
      setUserErrors({
        ...userErrors, 
        email: 'This must be in a valid email format'
      })
    } else{
      setUserErrors({...userErrors, email: ''})
      return true;
    }

    return false;
  }

  const validateLocation = () => {
    if(user.userType === userTypes.EXTERNAL && isEmpty(user.locations)){
      setLocationError('Required')
      return false;
    } else {
      setLocationError('')
      return true;
    }
  }

  const isFormValid = () => {
    let field: keyof UserType;

    for(field in userErrors){
      if(!isEmpty(userErrors[field])){
        return false;
      }   
    }
    
    if(!validateLocation())
      return false;
    
    if(!validateEmail())
      return false;

    return true;
  }

  const handleAddUserSubmitClick = () => {  
    if(!isFormValid()){  
      return;
    }
    
    Users.createUser(
      user.userName, 
      user.userType, 
      user.firstName, 
      user.lastName, 
      user.email, 
      user.password || '' 
    )
      .then((newUser) => {
        if(newUser.userType === userTypes.INTERNAL){
          setSaved(true)
        } else {
          user.locations.forEach((loc) => {
            Locations.addLocation(newUser.id, loc.id)
              .then((newLocation) => {
                setSaved(true)
              })
              .catch((e) => {
                setSaved(false)
                setServiceError(e.response.data.message)
              })
          })
        }       
      }).catch((e) => {
        setSaved(false)
        setServiceError(e.response.data.message)
      })
  }

  const handleLocationChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value:string = event.target.value as string;
    let userLocations: Location[] = []
    userLocations.push(...(locationList.filter(loc => loc.id === value)))

    setUser({...user, locations: userLocations})      
  }

  return (     
    <div>
      <SuccessDialog 
        open={saved && serviceError.length === 0}
        onClose={onCloseHandler}
        title='Add New User'
        content='New User Saved'
      />
      <Dialog 
        onClose={onCloseHandler} 
        aria-labelledby="add-user-title" 
        aria-describedby="add-user-text"
        open={open}
      >
        <DialogTitle id="add-user-title">Add New User</DialogTitle>
        <DialogContent>
          <DialogContentText id="add-user-text">   
            {serviceError.length > 0 
              &&
              <Grid item xs={12}>
                <Alert severity="error" >
                  <AlertTitle>Error</AlertTitle>
                  {serviceError}
                </Alert>   
                <Grid className={classes.gridSpacer} />
              </Grid>
            } 
            <TextField
              id="userName"
              label="User Name"
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={handleChange} 
              onBlur={validateField}
              error={!isEmpty(userErrors.userName)}
              helperText={userErrors.userName}
              required
            />
            <TextField
              id="firstName"
              label="First Name"
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={handleChange} 
              onBlur={validateField}
              error={!isEmpty(userErrors.firstName)}
              helperText={userErrors.firstName}
              required
            />
            <TextField
              id="lastName"
              label="Last Name"
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={handleChange} 
              onBlur={validateField}
              error={!isEmpty(userErrors.lastName)}
              helperText={userErrors.lastName}
              required
            />
            <TextField
              id="email"
              label="Email"
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={handleChange} 
              onBlur={validateEmail}
              error={!isEmpty(userErrors.email)}
              helperText={userErrors.email}
              required
            />
            <FormControl 
              variant="outlined" 
              margin="normal"
              fullWidth 
              error={!isEmpty(userErrors.userType)}   
              required
            >
              <InputLabel id="demo-simple-select-filled-label">User Type</InputLabel>
              <Select
                labelId="demo-simple-select-filled-label"
                id="demo-simple-select-filled"
                onChange={handleUserTypeChange} 
                onBlur={validateField}
                labelWidth={80}
                defaultValue="-1"
              >
                <MenuItem value="-1">
                  <em>Select User Type</em>
                </MenuItem>
                {Object.keys(userTypesString).map((userType) => (
                <MenuItem key={userType} value={userType}>
                  {userTypesString[userType as keyof typeof userTypesString]}
                </MenuItem>
              ))}
              </Select>
              <FormHelperText>{userErrors.userType}</FormHelperText>
            </FormControl>
            { user.userType === userTypes.EXTERNAL &&
              <FormControl 
                variant="outlined" 
                margin="normal"
                fullWidth 
                error={!isEmpty(locationError)}
                required
              >
                <InputLabel id="locationsLabel">Location</InputLabel>
                <Select
                  labelId="locationsLabel"
                  id="locationsSelect"
                  onChange={handleLocationChange} 
                  onBlur={validateLocation}
                  labelWidth={80}
                  defaultValue="-1"
                >
                  <MenuItem value="-1">
                    <em>Select Location</em>
                  </MenuItem>
                  {locationList.map((loc) => (
                    <MenuItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{locationError}</FormHelperText>
              </FormControl>
            }
            <FormControlLabel control={
              <Checkbox
                checked={user.enabled}
                onChange={handleEnabled}
                inputProps={{ 'aria-label': 'controlled' }}
                color="primary"
              />
            } label="Enable" />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={onCloseHandler}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            color="primary"
            size="small" 
            onClick={handleAddUserSubmitClick}
          >
            Submit 
          </Button>
        </DialogActions>
      </Dialog>   
    </div>
  );
};

export default CreateUserDialog;